import { QuillInstance, QuillStatic } from '../../types'

import {
  findEffectBlot,
  findClosestEffectEl,
  registerEffectBlot,
  findAllEffectBlotsInQuill,
} from './blot'
import { EFFECT_BLOT_NAME, EFFECT_MODULE_NAME } from './constants'
import { EffectOptions } from './types'

export const registerEffectModule = (Quill: QuillStatic) => {
  const EffectBlot = registerEffectBlot(Quill)

  class EffectModule {
    #quill: QuillInstance

    #rootEl: HTMLElement

    #options: EffectOptions

    constructor(quill: QuillInstance, options: EffectOptions = {}) {
      this.#quill = quill
      this.#rootEl = quill.root
      this.#options = options

      this.#observe()

      quill.root.addEventListener('click', (e) => {
        const target = e.target

        if (!target) {
          return
        }

        if (!(target instanceof HTMLElement)) {
          return
        }

        const closestEl = findClosestEffectEl(target)

        if (!closestEl) {
          return
        }

        const foundBlot = findEffectBlot(closestEl)

        if (!foundBlot) {
          return
        }

        if (!(foundBlot instanceof EffectBlot)) {
          return
        }

        /**
         * 클릭 이벤트 전파를 막아서 버튼이 클릭됐을 때 드롭다운이 닫히지 않도록 함
         */
        e.stopPropagation()

        options.onClick?.(closestEl, foundBlot.value().effect)
      })
    }

    /**
     * 현재 모듈이 활성화 되어 있는지 확인
     */
    get #isDestroyed() {
      const rootEl = this.#rootEl

      /**
       * 에디터 엘리멘트가 body에 마운트 되어 있는지로 에디터가 살아있는 상태인지 확인
       */
      return !rootEl.closest('body')
    }

    #getEffectClipList = () => {
      return this.#options.getEffectClipList?.() ?? []
    }

    #getEffectBlotList = () => {
      return this.#options.getEffectBlotList?.() ?? []
    }

    #refreshEffectBlotList = () => {
      const foundCurrentQuillEffectBlots = findAllEffectBlotsInQuill(
        this.#rootEl,
      )

      const changingEffectBlots: {
        [blotId: string]: {
          order?: number
          connected?: boolean
        }
      } = {}

      /**
       * 순서 정렬
       */
      const effectBlotList = this.#getEffectBlotList()
      const flattenEffectBlotList = effectBlotList.flat()

      for (let order = 0; order < flattenEffectBlotList.length; order++) {
        const currentBlotId = flattenEffectBlotList[order].id

        const currentBlot = foundCurrentQuillEffectBlots.find(
          (blot) => blot.value().effect.id === currentBlotId,
        )

        if (!currentBlot) {
          continue
        }

        const currentBlotValue = currentBlot.value().effect
        const currentBlotOrder = currentBlotValue.order

        if (currentBlotOrder === order) {
          continue
        }

        changingEffectBlots[currentBlotId] = {
          ...changingEffectBlots[currentBlotId],
          order,
        }
      }

      /**
       * 연결 상태 갱신
       */
      const effectClipList = this.#getEffectClipList()

      for (const currentBlot of foundCurrentQuillEffectBlots) {
        const currentBlotValue = currentBlot.value().effect
        const currentBlotId = currentBlotValue.id

        const connected = effectClipList.includes(currentBlotValue.effectClipId)

        if (currentBlotValue.connected === connected) {
          continue
        }

        changingEffectBlots[currentBlotId] = {
          ...changingEffectBlots[currentBlotId],
          connected,
        }
      }

      /**
       * 변경 사항 적용
       */
      for (const [blotId, changing] of Object.entries(changingEffectBlots)) {
        const currentBlot = foundCurrentQuillEffectBlots.find(
          (blot) => blot.value().effect.id === blotId,
        )

        if (!currentBlot) {
          continue
        }

        const currentBlotValue = currentBlot.value().effect

        const newBlotValue = {
          ...currentBlotValue,
          ...changing,
        }

        currentBlot.replaceWith(EFFECT_BLOT_NAME, newBlotValue)
      }
    }

    /**
     * 이펙트 블롯 상태 갱신 루프
     */
    #observe = () => {
      const intervalId = setInterval(() => {
        /**
         * 에디터 엘리멘트가 body에 마운트 되어 있는지로 에디터가 살아있는 상태인지 확인
         */
        const isDestroyed = this.#isDestroyed

        if (isDestroyed) {
          clearInterval(intervalId)
          return
        }

        this.#refreshEffectBlotList()
        /**
         * FIXME: 시간 간격 다시 보기
         */
      }, 1000)
    }
  }

  Quill.register(`modules/${EFFECT_MODULE_NAME}`, EffectModule, true)

  return EffectModule
}
